/**
 * Dataset de docs locales — JS/TS/Web APIs comunes que el candidato puede
 * necesitar durante el test. NO requiere internet, está bundleado en el JS.
 *
 * El search hace match en title, summary y tags (substring case-insensitive).
 */

export interface DocEntry {
  id: string;
  title: string;
  category: "Estructuras de datos" | "Async" | "Funciones" | "Iteración" | "Utilidades" | "Web APIs";
  summary: string;
  example: string;
  tags: string[];
}

export const LOCAL_DOCS: DocEntry[] = [
  {
    id: "map",
    title: "Map",
    category: "Estructuras de datos",
    summary:
      "Colección clave-valor que mantiene orden de inserción. Acepta cualquier tipo de clave (strings, objects, etc.).",
    example: `const m = new Map<string, number>();
m.set("a", 1);
m.set("b", 2);
m.get("a");           // 1
m.has("c");           // false
m.size;               // 2
m.delete("a");
for (const [k, v] of m) {
  console.log(k, v);
}`,
    tags: ["map", "dict", "diccionario", "key-value", "estructura"],
  },
  {
    id: "set",
    title: "Set",
    category: "Estructuras de datos",
    summary: "Colección de valores únicos. Usa igualdad estricta (===).",
    example: `const s = new Set<number>([1, 2, 3, 2]);
s.size;        // 3
s.has(2);      // true
s.add(4);
s.delete(1);
[...s];        // [2, 3, 4]`,
    tags: ["set", "unique", "únicos", "deduplicar"],
  },
  {
    id: "array",
    title: "Array",
    category: "Estructuras de datos",
    summary:
      "Métodos más usados: map, filter, reduce, forEach, find, some, every, sort, slice, splice.",
    example: `const a = [1, 2, 3, 4];
a.map(x => x * 2);              // [2,4,6,8]
a.filter(x => x % 2 === 0);     // [2,4]
a.reduce((acc, x) => acc + x, 0); // 10
a.find(x => x > 2);             // 3
a.some(x => x > 3);             // true
a.every(x => x > 0);            // true
a.sort((a, b) => a - b);        // numeric sort
[...new Set(a)];                // dedupe`,
    tags: ["array", "map", "filter", "reduce", "find", "some", "every", "sort"],
  },
  {
    id: "object",
    title: "Object",
    category: "Estructuras de datos",
    summary: "Estructura clave-valor con keys string/symbol. Útil para objetos planos.",
    example: `const o = { a: 1, b: 2 };
Object.keys(o);                 // ["a","b"]
Object.values(o);               // [1,2]
Object.entries(o);              // [["a",1],["b",2]]
Object.fromEntries([["a",1]]);  // { a: 1 }
const copy = { ...o, c: 3 };
delete copy.a;`,
    tags: ["object", "keys", "values", "entries", "spread"],
  },
  {
    id: "promise",
    title: "Promise",
    category: "Async",
    summary: "Representa el resultado de una operación asíncrona.",
    example: `const p = new Promise<number>((resolve, reject) => {
  setTimeout(() => resolve(42), 100);
});
p.then(v => console.log(v));
await p;

// Combinadores
await Promise.all([p1, p2]);
await Promise.race([p1, p2]);
await Promise.allSettled([p1, p2]);
Promise.resolve(1);
Promise.reject(new Error("nope"));`,
    tags: ["promise", "async", "then", "await", "all", "race"],
  },
  {
    id: "async",
    title: "async / await",
    category: "Async",
    summary: "Azúcar sintáctica sobre Promises. async function devuelve siempre una Promise.",
    example: `async function fetchUser(id: string) {
  const res = await fetch(\`/users/\${id}\`);
  if (!res.ok) throw new Error("Failed");
  return res.json();
}

try {
  const u = await fetchUser("1");
} catch (e) {
  console.error(e);
}`,
    tags: ["async", "await", "promise"],
  },
  {
    id: "date",
    title: "Date",
    category: "Utilidades",
    summary: "Fecha + hora. Mejor usar timestamps (Date.now()) para cálculos.",
    example: `Date.now();                   // ms desde epoch
const d = new Date();
d.getTime();                  // ms desde epoch
d.toISOString();              // "2026-05-13T..."
new Date(2026, 4, 13);        // mes 0-indexed!
const future = new Date(Date.now() + 60_000);  // +1 min`,
    tags: ["date", "fecha", "now", "timestamp", "iso"],
  },
  {
    id: "performance",
    title: "performance.now()",
    category: "Web APIs",
    summary:
      "Timestamp de alta resolución en ms (con decimales). Mejor que Date.now() para medir tiempos cortos.",
    example: `const start = performance.now();
doSomething();
const elapsed = performance.now() - start;
console.log(\`Tardó \${elapsed.toFixed(2)}ms\`);`,
    tags: ["performance", "now", "timer", "benchmark", "elapsed"],
  },
  {
    id: "class",
    title: "Class",
    category: "Funciones",
    summary: "Clase con constructor, métodos, herencia, modificadores public/private/protected (TS).",
    example: `class Animal {
  constructor(protected name: string) {}
  greet() { return \`Hi, I'm \${this.name}\`; }
}

class Dog extends Animal {
  constructor(name: string, private breed: string) {
    super(name);
  }
  greet() {
    return super.greet() + \`, a \${this.breed}\`;
  }
}

const d = new Dog("Rex", "Labrador");
d.greet();`,
    tags: ["class", "constructor", "extends", "super", "private", "public"],
  },
  {
    id: "json",
    title: "JSON",
    category: "Utilidades",
    summary: "Serializar y deserializar valores JS.",
    example: `JSON.stringify({ a: 1, b: [1, 2] });
JSON.stringify(obj, null, 2);   // pretty print
JSON.parse('{"a":1}');
JSON.parse('{"a":1}', (key, val) => key === "" ? val : val * 2);`,
    tags: ["json", "stringify", "parse", "serializar"],
  },
  {
    id: "number",
    title: "Number",
    category: "Utilidades",
    summary: "Conversión y formateo de números.",
    example: `Number("42");                 // 42
parseInt("42px", 10);         // 42
parseFloat("3.14");           // 3.14
Number.isInteger(2);          // true
Number.isFinite(Infinity);    // false
(3.14159).toFixed(2);         // "3.14"
Math.round(3.7);              // 4
Math.floor(3.7);              // 3
Math.max(1, 5, 3);            // 5`,
    tags: ["number", "parseint", "parsefloat", "math", "round", "floor"],
  },
  {
    id: "string",
    title: "String",
    category: "Utilidades",
    summary: "Métodos de strings más usados.",
    example: `"hello".length;               // 5
"hello".toUpperCase();        // "HELLO"
"hello".includes("ell");      // true
"hello".indexOf("l");         // 2
"hello".slice(1, 3);          // "el"
"hello".split("");            // ["h","e","l","l","o"]
"  hi  ".trim();              // "hi"
"a,b,c".split(",");           // ["a","b","c"]
\`Hello \${name}\`;             // template literal`,
    tags: ["string", "split", "slice", "includes", "template"],
  },
  {
    id: "regex",
    title: "RegExp",
    category: "Utilidades",
    summary: "Expresiones regulares. Útiles para validación y parseo simple.",
    example: `const re = /^(\\d{4})-(\\d{2})-(\\d{2})$/;
re.test("2026-01-15");        // true
"2026-01-15".match(re);       // ["2026-01-15","2026","01","15"]
"a, b, c".split(/, ?/);       // ["a","b","c"]
"hello".replace(/l/g, "L");   // "heLLo"`,
    tags: ["regex", "regexp", "match", "test", "replace"],
  },
  {
    id: "iteration",
    title: "Iteración (for..of, for..in)",
    category: "Iteración",
    summary: "for..of itera valores. for..in itera keys (evitarlo en arrays).",
    example: `// Array / Set / Map → for..of
for (const x of [1, 2, 3]) {}
for (const [k, v] of new Map()) {}

// Object → entries
for (const [k, v] of Object.entries(obj)) {}

// Indexado
for (let i = 0; i < arr.length; i++) {}`,
    tags: ["for", "of", "iterar", "iteración", "loop"],
  },
  {
    id: "destructuring",
    title: "Destructuring",
    category: "Funciones",
    summary: "Desestructurar arrays y objetos para extraer valores.",
    example: `const { a, b: bAlias, c = 0 } = { a: 1, b: 2 };
const [first, , third, ...rest] = [1, 2, 3, 4, 5];

function f({ x, y }: { x: number; y: number }) {
  return x + y;
}`,
    tags: ["destructuring", "destructurar", "spread", "rest"],
  },
  {
    id: "weakmap",
    title: "WeakMap / WeakSet",
    category: "Estructuras de datos",
    summary:
      "Map/Set cuyas claves son objetos referencia débil (no impiden GC). Útil para cachés de objetos.",
    example: `const cache = new WeakMap<object, string>();
const obj = {};
cache.set(obj, "data");
cache.get(obj);                // "data"
// Cuando obj se garbage-collect, su entrada se libera.`,
    tags: ["weakmap", "weakset", "weak", "gc", "cache"],
  },
];

/** Búsqueda case-insensitive sobre title, summary, tags y category. */
export function searchDocs(query: string): DocEntry[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return LOCAL_DOCS.filter((d) => {
    if (d.title.toLowerCase().includes(q)) return true;
    if (d.summary.toLowerCase().includes(q)) return true;
    if (d.category.toLowerCase().includes(q)) return true;
    if (d.tags.some((t) => t.toLowerCase().includes(q))) return true;
    return false;
  });
}
